import { UnitHistory } from '../unit-specific-facts';

type TimeFacts = {
  [key: string]: UnitHistory;
};

export const timeFacts: TimeFacts = {
  second: {
    name: "Second",
    shortDescription: "The base unit of time, defined by atomic oscillations",
    history: "The journey of the second's definition reflects humanity's quest for ever-more-precise timekeeping. Originally defined as 1/86,400 of a mean solar day, it evolved through mechanical pendulums and quartz crystals before reaching today's incredible precision with atomic clocks. This shift from astronomical to atomic time keeping represents one of measurement history's most dramatic precision improvements.",
    origin: "From ancient divisions of the day through Babylonian sexagesimal (base-60) mathematics, refined over millennia",
    modernUsage: "Modern atomic clocks now define the second using the oscillations of cesium-133 atoms, achieving precision that would neither gain nor lose a second over 100 million years",
    funFacts: [
      "A cesium atomic clock must count exactly 9,192,631,770 oscillations to measure one second - a number chosen to match the historical solar-based second",
      "The invention of the pendulum clock in 1656 suddenly made it possible to measure seconds reliably, revolutionizing science and navigation",
      "Ancient Egyptian shadow clocks could measure time to the nearest hour, but it took nearly 3,000 years to accurately measure individual seconds"
    ],
    related: ["minute", "hour", "day"],
    images: []
  },
  moment: {
    name: "Moment",
    shortDescription: "Originally a medieval unit equal to 90 seconds",
    history: "The medieval moment lasted 90 seconds, or 1/40 of an hour - a duration chosen for its mathematical convenience in astronomical calculations. The word derives from the Latin 'momentum,' meaning 'movement' or 'moving power.' Medieval astronomers used water clocks and astronomical tables to measure moments.",
    origin: "From Latin 'momentum' meaning 'movement' or 'moving power'",
    modernUsage: "Historical interest only; modern use is colloquial",
    funFacts: [
      "Exactly 90 seconds in medieval times",
      "Used in astronomical calculations",
      "Modern meaning differs from historical definition"
    ],
    related: ["second", "minute", "hour"],
    images: []
  },
  fortnight: {
    name: "Fortnight",
    shortDescription: "A period of fourteen days (two weeks)",
    history: "The fortnight (fourteen nights) originated in English-speaking cultures as a convenient halfway point between week and month. The term reflects ancient Germanic people's practice of counting nights rather than days. The fortnight gained particular importance in medieval law, where it often specified waiting periods for legal actions.",
    origin: "From Old English, reflecting Germanic practice of counting nights",
    modernUsage: "Still commonly used in British English for recurring periods",
    funFacts: [
      "Name literally means 'fourteen nights'",
      "Used in medieval law for waiting periods",
      "Still common in British payroll and rental periods"
    ],
    related: ["week", "month", "day"],
    images: []
  },
  jiffy: {
    name: "Jiffy",
    shortDescription: "A technical term for a very brief, precisely defined time interval",
    history: "Originally a technical term from electronics, denoting the time between alternating current power cycles (1/60 or 1/50 of a second depending on the power system), the jiffy later found new meaning in computer science. Early programmers defined it as 1/100 of a second, though modern computing uses various definitions depending on the operating system.",
    origin: "From electrical engineering, later adopted by computer science",
    modernUsage: "Used in computing for system timing measurements",
    funFacts: [
      "Different definitions in electronics and computing",
      "Can mean 1/100th second in some systems",
      "Originally based on AC power cycles"
    ],
    related: ["second", "moment"],
    images: []
  },
  minute: {
    name: "Minute",
    shortDescription: "One-sixtieth of an hour, derived from ancient Babylonian mathematics",
    history: "The minute originated from the ancient Babylonian sexagesimal (base-60) number system. Early astronomers like Ptolemy divided the day into 24 hours, each hour into 60 minutes, and each minute into 60 seconds. This division became standardized through medieval astronomical tables.",
    origin: "From Latin 'pars minuta prima', meaning 'first small part'",
    modernUsage: "Fundamental unit of time measurement in modern society",
    funFacts: [
      "Based on Babylonian base-60 system",
      "Name means 'first small part'",
      "Standardized by medieval astronomers"
    ],
    related: ["hour", "second"],
    images: []
  },
  hour: {
    name: "Hour",
    shortDescription: "One twenty-fourth of a day, based on ancient Egyptian timekeeping",
    history: "The 24-hour day originated in ancient Egypt, where time was divided into 12 daytime hours and 12 nighttime hours. The Greeks and Romans adopted this system, though their hours varied in length with the seasons. Fixed-length hours became standard with mechanical clocks in medieval Europe.",
    origin: "From Greek 'hora' via Latin 'hora', originally meaning 'season' or 'time of day'",
    modernUsage: "Standard unit for daily timekeeping worldwide",
    funFacts: [
      "Originally varied in length with seasons",
      "Egyptians used 12 hours of day and night",
      "Standardized by mechanical clocks"
    ],
    related: ["minute", "day"],
    images: []
  },
  day: {
    name: "Day",
    shortDescription: "The time period of one complete rotation of Earth",
    history: "The day as a time unit is based on Earth's rotation. Ancient civilizations divided the day-night cycle into smaller units, leading to various systems. The modern mean solar day was standardized in the 19th century, though atomic time has since refined its definition.",
    origin: "From Old English 'dæg', related to ancient words for 'to burn' (referring to daylight)",
    modernUsage: "Basic unit of civil timekeeping and calendar systems",
    funFacts: [
      "Actually getting longer by 1.8 milliseconds per century",
      "Varies slightly due to Earth's elliptical orbit",
      "Different lengths on different planets"
    ],
    related: ["hour", "week"],
    images: []
  },
  week: {
    name: "Week",
    shortDescription: "A period of seven days, rooted in ancient traditions",
    history: "The seven-day week originated in ancient Mesopotamia, based on celestial observations. The Romans adopted it from Jewish tradition, and it spread through Europe with Christianity. The names of weekdays reflect ancient associations with celestial bodies and deities.",
    origin: "From Old English 'wice', related to words meaning 'to turn' or 'succession'",
    modernUsage: "Universal unit for scheduling and organization in modern society",
    funFacts: [
      "Based on lunar phases originally",
      "Different lengths in some ancient cultures",
      "Standardized globally through colonialism"
    ],
    related: ["day", "month"],
    images: []
  },
  month: {
    name: "Month",
    shortDescription: "Originally based on the Moon's orbit around Earth",
    history: "The month derived from the Moon's orbital period. Ancient calendars used lunar months of 29 or 30 days. Julius Caesar's calendar reform established months of fixed lengths, slightly divorced from lunar cycles. This became the basis for our modern calendar months.",
    origin: "From Old English 'monath', related to 'moon'",
    modernUsage: "Standard unit in modern calendars and financial systems",
    funFacts: [
      "Lunar month is actually 29.53 days",
      "Romans added July and August",
      "Some cultures still use lunar months"
    ],
    related: ["week", "year"],
    images: []
  },
  year: {
    name: "Year",
    shortDescription: "The period of Earth's orbit around the Sun",
    history: "The year was originally determined by observing seasonal changes and celestial movements. Ancient civilizations developed various calendar systems to track years. The modern calendar year was standardized through the Gregorian reform of 1582, accounting for the actual solar year of 365.242199 days.",
    origin: "From Old English 'gear', possibly related to Greek 'hōros' meaning 'year' or 'season'",
    modernUsage: "Fundamental unit for long-term time measurement and calendar systems",
    funFacts: [
      "Solar year is actually 365.242199 days",
      "Leap years added by Julius Caesar",
      "Different lengths on different planets"
    ],
    related: ["month", "day"],
    images: []
  }
};
